/**
 * Coaching style — Ben Petty voice note 2026-05-05 (afternoon, note 2).
 *
 * Three named styles for the grassroots conversation. Optional input at
 * grassroots — coaches CAN pick one to bias session generation, but the
 * default UX is "no style preference" (the AI plans a balanced session
 * focused on player development). Don't force a rigid style too early
 * — at grassroots the priority is the player, not the team's identity.
 *
 * Plain-English UX (note 2 verbatim): "The coach might be able to say
 * for example into the app that he wants to play forward quickly. So
 * that would then fit into the direct style." `inferCoachingStyle()`
 * is the regex layer for that mapping; a Haiku-class fallback can sit
 * on top later for phrases the regex misses.
 *
 * Style is INDEPENDENT from phase-of-play `primaryFocus`. Phase says
 * *what to work on* (e.g. pressing). Style says *how the team wants
 * to play in general*. Both can influence the AI session generator.
 */

export const COACHING_STYLES = ['possession_based', 'direct_play', 'counter_attacking'] as const;
export type CoachingStyle = typeof COACHING_STYLES[number];

export interface CoachingStyleMeta {
  id: CoachingStyle;
  /** Coach-facing label for chip picker. */
  label: string;
  /** One-line description in coach language. */
  description: string;
  /** Single-character glyph for compact chips. */
  glyph: string;
  /** Distinct colour family per style — distinct from phase + corner
   *  palettes so a coach can scan a session card and read all three
   *  axes (phase / style / corner emphasis) at once. */
  colorHex: string;
  /** Example coach phrases that map to this style. Doubles as
   *  classifier training data AND as UI hints in a "for example..."
   *  affordance under the chip picker. */
  examplePhrases: string[];
}

export const COACHING_STYLE_META: Record<CoachingStyle, CoachingStyleMeta> = {
  possession_based: {
    id: 'possession_based',
    label: 'Possession-based',
    description: 'Keep the ball, build through the thirds, control the tempo.',
    glyph: '○',
    colorHex: '#16A085',
    examplePhrases: [
      'keep the ball',
      'play out from the back',
      'play through midfield',
      'control possession',
      'build up',
      'pass and move',
    ],
  },
  direct_play: {
    id: 'direct_play',
    label: 'Direct play',
    description: 'Play forward quickly — get the ball into the box without delay.',
    glyph: '→',
    colorHex: '#E67E22',
    examplePhrases: [
      'play forward quickly',
      'go direct',
      'long balls',
      'get it forward',
      'verticality',
      'play to the striker',
    ],
  },
  counter_attacking: {
    id: 'counter_attacking',
    label: 'Counter-attacking',
    description: 'Sit back, win the ball, break in numbers.',
    glyph: '↺',
    colorHex: '#8E44AD',
    examplePhrases: [
      'win it back high',
      'on the break',
      'counter',
      'fast break',
      'absorb pressure',
      'hit them on the break',
    ],
  },
};

/**
 * Heuristic — given a free-text phrase the coach typed, classify it to one
 * of the three styles. Returns null when nothing matches confidently.
 *
 * Order matters — direct_play is checked before possession_based because
 * "play forward" appears in both ("play forward quickly" → direct;
 * "play forward through the thirds" is closer to possession but the
 * verb-phrase "play forward" is the stronger direct-style signal). Coach
 * intent is usually clearer when we lean toward direct on the ambiguous
 * case — a coach who wanted possession would more likely say "keep the
 * ball" or "build up" first.
 *
 * Tiki-taka maps to possession_based (it IS a possession style) but the
 * Pep/Klopp anti-pattern check (Ben note 2: "the technique isn't there
 * to do that, so they just get frustrated") is a separate concern handled
 * by the discipline-imposer — see status/4cm.md item 5.
 */
export function inferCoachingStyle(text: string): CoachingStyle | null {
  const t = text.toLowerCase();

  // Direct play — fast/forward verticality language.
  if (/\b(direct|verticality|long balls?|forward quickly|get it forward|play to the striker|play forward)\b/.test(t)) {
    return 'direct_play';
  }

  // Counter-attacking — turnover-led break language.
  if (/\b(counter|counter-attack|on the break|fast break|win it (back )?high|absorb pressure|hit them on the break)\b/.test(t)) {
    return 'counter_attacking';
  }

  // Possession-based — control / build / keep / tiki-taka.
  if (/\b(possession|keep the ball|play out|build.?up|build.?out|through the thirds|control(\s+possession)?|tiki.?taka|pass and move)\b/.test(t)) {
    return 'possession_based';
  }

  return null;
}

/** Validate a candidate style value (e.g. coming from an API client or
 *  AI tool input). Returns the validated CoachingStyle or null when
 *  the input isn't one of the three canonical values. */
export function parseCoachingStyle(value: unknown): CoachingStyle | null {
  if (typeof value !== 'string') return null;
  return (COACHING_STYLES as readonly string[]).includes(value)
    ? (value as CoachingStyle)
    : null;
}
